import { CertificateMechanicalValue, MechanicalProperty } from '../types';

export type ValidationStatus = 'ok' | 'warning' | 'error' | 'missing';

export interface MetalValidationResult {
  name: string;
  measuredValue: number;
  minValue?: number;
  maxValue?: number;
  status: ValidationStatus;
  statusMessage: string;
}

interface MechanicalValidationResult {
  name: string;
  measuredValue: number;
  minValue?: number;
  maxValue?: number;
  unit?: string;
  status: ValidationStatus;
  statusMessage: string;
}

interface StatusSummary {
  total: number;
  ok: number;
  error: number;
  warning: number;
  missing: number;
}

export function getStatusColor(status: ValidationStatus): string {
  switch (status) {
    case 'ok':
      return 'success.main';
    case 'warning':
      return 'warning.main';
    case 'error':
      return 'error.main';
    case 'missing':
      return 'grey.500';
  }
}

export function getStatusChipColor(
  status: ValidationStatus
): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'ok':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'missing':
      return 'default';
  }
}

function validateMetalValue(
  metalValue: { metal_name: string; measured_percentage: number },
  compositions: { metal_name: string; min_percentage: number; max_percentage: number }[]
): MetalValidationResult {
  const composition = compositions.find(
    (comp) => comp.metal_name.toLowerCase() === metalValue.metal_name.toLowerCase()
  );

  if (!composition) {
    return {
      name: metalValue.metal_name,
      measuredValue: metalValue.measured_percentage,
      status: 'warning',
      statusMessage: 'Nicht in Spezifikation',
    };
  }

  const { min_percentage, max_percentage } = composition;
  const value = metalValue.measured_percentage;
  const isInRange = value >= min_percentage && value <= max_percentage;

  return {
    name: metalValue.metal_name,
    measuredValue: value,
    minValue: min_percentage,
    maxValue: max_percentage,
    status: isInRange ? 'ok' : 'error',
    statusMessage: isInRange ? 'Im Bereich' : value < min_percentage ? 'Zu niedrig' : 'Zu hoch',
  };
}

export function getMetalValidationResults(
  metalValues: { metal_name: string; measured_percentage: number }[],
  compositions: { metal_name: string; min_percentage: number; max_percentage: number }[]
): MetalValidationResult[] {
  const measured = metalValues.map((mv) => validateMetalValue(mv, compositions));

  const missing: MetalValidationResult[] = compositions
    .filter(
      (comp) =>
        !metalValues.some((mv) => mv.metal_name.toLowerCase() === comp.metal_name.toLowerCase())
    )
    .map((comp) => ({
      name: comp.metal_name,
      measuredValue: 0,
      minValue: comp.min_percentage,
      maxValue: comp.max_percentage,
      status: 'missing' as const,
      statusMessage: 'Nicht gemessen',
    }));

  return [...measured, ...missing].sort((a, b) => a.name.localeCompare(b.name));
}

function validateMechanicalValue(
  mechanicalValue: CertificateMechanicalValue,
  properties: MechanicalProperty[]
): MechanicalValidationResult {
  const property = properties.find(
    (prop) => prop.property_name.toLowerCase() === mechanicalValue.property_name.toLowerCase()
  );

  if (!property) {
    return {
      name: mechanicalValue.property_name,
      measuredValue: mechanicalValue.measured_value,
      unit: mechanicalValue.unit,
      status: 'warning',
      statusMessage: 'Nicht in Spezifikation',
    };
  }

  const { min_value, max_value } = property;
  const value = mechanicalValue.measured_value;

  const belowMin = min_value != null && value < min_value;
  const aboveMax = max_value != null && value > max_value;
  const isInRange = !belowMin && !aboveMax;

  let statusMessage = 'Im Bereich';
  if (!isInRange) {
    if (belowMin && aboveMax) {
      statusMessage = 'zu niedrig und zu hoch';
    } else if (belowMin) {
      statusMessage = 'zu niedrig';
    } else {
      statusMessage = 'zu hoch';
    }
  }

  return {
    name: mechanicalValue.property_name,
    measuredValue: value,
    minValue: min_value ?? undefined,
    maxValue: max_value ?? undefined,
    unit: mechanicalValue.unit || property.unit,
    status: isInRange ? 'ok' : 'error',
    statusMessage,
  };
}

export function getMechanicalValidationResults(
  mechanicalValues: CertificateMechanicalValue[],
  properties: MechanicalProperty[]
): MechanicalValidationResult[] {
  const measured = mechanicalValues.map((mv) => validateMechanicalValue(mv, properties));

  const missing: MechanicalValidationResult[] = properties
    .filter(
      (prop) =>
        !mechanicalValues.some(
          (mv) => mv.property_name.toLowerCase() === prop.property_name.toLowerCase()
        )
    )
    .map((prop) => ({
      name: prop.property_name,
      measuredValue: 0,
      minValue: prop.min_value ?? undefined,
      maxValue: prop.max_value ?? undefined,
      unit: prop.unit,
      status: 'missing' as const,
      statusMessage: 'Nicht gemessen',
    }));

  return [...measured, ...missing].sort((a, b) => a.name.localeCompare(b.name));
}

export function getStatusSummary(results: Array<{ status: ValidationStatus }>): StatusSummary {
  const summary: StatusSummary = { total: results.length, ok: 0, error: 0, warning: 0, missing: 0 };
  for (const r of results) {
    summary[r.status]++;
  }
  return summary;
}

export function getQuickMetalStatus(
  metalValues: { metal_name: string; measured_percentage: number }[],
  compositions: { metal_name: string; min_percentage: number; max_percentage: number }[]
): { status: ValidationStatus | 'none'; label: string } {
  if (metalValues.length === 0) {
    return { status: 'none', label: 'Keine Werte' };
  }

  const results = getMetalValidationResults(metalValues, compositions);
  const summary = getStatusSummary(results);

  if (summary.error > 0) {
    return { status: 'error', label: `${summary.error} außerhalb` };
  }
  if (summary.missing > 0) {
    return { status: 'warning', label: `${summary.missing} fehlend` };
  }
  if (summary.ok > 0) {
    return { status: 'ok', label: 'Alle OK' };
  }
  return { status: 'warning', label: 'Unbekannt' };
}
